import { Button } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";

import Plot, { createTimeHoverProps } from "../components/ThreediPlot";
import DownloadCsvIcon from "../icons/download_csv_icon.svg?react";
import { getWaterFlowChart } from "../state/selectedAsset";
import { getSimulation } from "../state/simulation";
import { Asset } from "../types/assets";

import { PropertyTableHeader } from "./PropertyTableHeader";
import { exportToCSV, getFilename } from "./TimeseriesChart";

interface Props {
  asset: Asset;
}

const TimeseriesFlowChart: React.FC<Props> = (props) => {
  const simulationName = useSelector(getSimulation).name;

  const waterTimeseriesGraph = useSelector(getWaterFlowChart);
  const velocityArray = (waterTimeseriesGraph || []).map((element) => element.velocity);
  const dischargeArray = (waterTimeseriesGraph || []).map((element) => element.discharge);
  const timestamps = (waterTimeseriesGraph || []).map((element) => element.timestamp);
  const timeOfWaterHeight = (waterTimeseriesGraph || []).map((element) => {
    const originalDate = new Date("2020-01-01T00:00:00Z");
    originalDate.setSeconds(originalDate.getSeconds() + element.timestamp);
    return originalDate.toISOString();
  });
  const lastTimeWaterTimeseriesGraph = (function () {
    const originalDate = new Date("2020-01-01T00:00:00Z");
    if (waterTimeseriesGraph.length && waterTimeseriesGraph[waterTimeseriesGraph.length - 1]) {
      const lastValue = waterTimeseriesGraph[waterTimeseriesGraph.length - 1];
      originalDate.setSeconds(originalDate.getSeconds() + lastValue.timestamp + 900);
    }
    return originalDate.toISOString();
  })();

  const intl = useIntl();

  return (
    <div>
      <PropertyTableHeader
        button={
          <Button
            variant="secondary"
            leftIcon={<DownloadCsvIcon />}
            onClick={() =>
              exportToCSV(
                "Velocity",
                timestamps,
                velocityArray,
                getFilename(simulationName, props.asset.properties.site_id, "Velocity")
              )
            }
          >
            CSV
          </Button>
        }
      >
        <FormattedMessage
          id="timeseriesWaterFlowChart.chartTitleVelocity"
          defaultMessage="Velocity (m/s)"
        />
      </PropertyTableHeader>
      <Plot
        style={{
          position: "relative",
          display: "inline-block",
          border: "1px solid #707070",
          borderRadius: 5,
          overflow: "hidden",
          marginBottom: 10,
        }}
        data={[
          {
            ...createTimeHoverProps(timeOfWaterHeight),
            y: velocityArray,
            type: "scatter",
            mode: "lines+markers",
            marker: { color: "#699fbe" },
          },
        ]}
        layout={{
          showlegend: false,
          width: 335,
          height: 335,
          paper_bgcolor: "#f2f2ec",
          plot_bgcolor: "#f2f2ec",
          margin: {
            l: 40,
            r: 10,
            b: 10,
            t: 10,
          },
          xaxis: {
            tickformat:
              "%H:%M\n" +
              intl.formatMessage({
                id: "timeseriesWaterFlowChart.chartXAxisLabel",
                defaultMessage: "day",
              }) +
              "%_d",
            // solution from // https://github.com/plotly/plotly.js/issues/1464 that didnot work
            // tickformat: "bla###%H:%M",
            title: {
              text: intl.formatMessage({
                id: "timeseriesWaterFlowChart.chartXAxisTime",
                defaultMessage: "Time (hours : mins) &#8594;",
              }),
              standoff: 10,
            },
            showticklabels: timeOfWaterHeight.length > 0,
            zeroline: false,
            automargin: true,
            range: [timeOfWaterHeight[0], lastTimeWaterTimeseriesGraph],
          },
          yaxis: {
            title: {
              text: "(m/s) &#8594;",
              standoff: 5,
            },
            exponentformat: "power",
            showline: true,
            zeroline: false,
            automargin: true,
          },
        }}
      />
      <PropertyTableHeader
        button={
          <Button
            variant="secondary"
            leftIcon={<DownloadCsvIcon />}
            onClick={() =>
              exportToCSV(
                "Discharge",
                timestamps,
                dischargeArray,
                getFilename(simulationName, props.asset.properties.site_id, "Discharge")
              )
            }
          >
            CSV
          </Button>
        }
      >
        <FormattedMessage
          id="timeseriesWaterFlowChart.chartTitleDischarge"
          defaultMessage="Discharge (m³/s)"
        />
      </PropertyTableHeader>
      <Plot
        style={{
          position: "relative",
          display: "inline-block",
          border: "1px solid #707070",
          borderRadius: 5,
          overflow: "hidden",
        }}
        data={[
          {
            ...createTimeHoverProps(timeOfWaterHeight),
            x: timeOfWaterHeight,
            y: dischargeArray,
            type: "scatter",
            mode: "lines+markers",
            marker: { color: "#699fbe" },
          },
        ]}
        layout={{
          showlegend: false,
          width: 335,
          height: 335,
          paper_bgcolor: "#f2f2ec",
          plot_bgcolor: "#f2f2ec",
          margin: {
            l: 40,
            r: 10,
            b: 10,
            t: 10,
          },
          xaxis: {
            tickformat:
              "%H:%M\n" +
              intl.formatMessage({
                id: "timeseriesWaterFlowChart.chartXAxisLabel",
                defaultMessage: "day",
              }) +
              "%_d",
            // solution from // https://github.com/plotly/plotly.js/issues/1464 that didnot work
            // tickformat: "bla###%H:%M",
            title: {
              text: intl.formatMessage({
                id: "timeseriesWaterFlowChart.chartXAxisTime",
                defaultMessage: "Time (hours : mins) &#8594;",
              }),
              standoff: 10,
            },
            showticklabels: timeOfWaterHeight.length > 0,
            zeroline: false,
            automargin: true,
            range: [timeOfWaterHeight[0], lastTimeWaterTimeseriesGraph],
          },
          yaxis: {
            title: {
              text: "(m³/s) &#8594;",
              standoff: 5,
            },
            exponentformat: "power",
            showline: true,
            zeroline: false,
            automargin: true,
          },
        }}
        config={{
          displayModeBar: false,
        }}
      />
    </div>
  );
};

export default TimeseriesFlowChart;
